import React, { useState, useEffect } from "react";
import axios from "axios";

const LogoEditor = () => {
  const [logoData, setLogoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const fetchLogoData = async () => {
      try {
        const response = await axios.get("https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/nav-logo");
        setLogoData(response.data.data);
      } catch (err) {
        setError("Failed to load logo data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogoData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setShowSuccess(false);
    try {
      const response = await axios.put("https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/nav-logo", logoData);
      setLogoData(response.data.data);
      setShowSuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError("Failed to update logo");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setLogoData((prev) => ({
      ...prev,
      [name]: val,
    }));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger d-flex align-items-center" role="alert">
        <i className="bi bi-exclamation-triangle-fill me-2"></i>
        <div>{error}</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-lg-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3">
              <h2 className="h4 mb-0 text-primary">
                <i className="bi bi-image me-2"></i>Manage Logo
              </h2>
            </div>
            
            <div className="card-body">
              {showSuccess && (
                <div className="alert alert-success d-flex align-items-center mb-4" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  <div>Logo updated successfully!</div>
                </div>
              )}
              
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="mb-4">
                    <label htmlFor="imageUrl" className="form-label fw-medium">
                      Image URL
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-link-45deg"></i>
                      </span>
                      <input
                        type="url"
                        className="form-control"
                        id="imageUrl"
                        name="imageUrl"
                        value={logoData.imageUrl}
                        onChange={handleChange}
                        placeholder="https://example.com/logo.png"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="altText" className="form-label fw-medium">
                      Alt Text
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-fonts"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="altText"
                        name="altText"
                        value={logoData.altText}
                        onChange={handleChange}
                        placeholder="Company Logo"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="mb-4">
                    <label htmlFor="linkUrl" className="form-label fw-medium">
                      Link URL
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-box-arrow-up-right"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="linkUrl"
                        name="linkUrl"
                        value={logoData.linkUrl}
                        onChange={handleChange}
                        placeholder="/"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="form-check form-switch mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={logoData.isActive}
                        onChange={handleChange}
                      />
                      <label className="form-check-label fw-medium" htmlFor="isActive">
                        Active
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border-top pt-4 mt-4">
                <h3 className="h5 mb-3">
                  <i className="bi bi-eye me-2"></i>Preview
                </h3>
                <div className="d-flex align-items-center justify-content-center bg-light rounded p-4" style={{ minHeight: '120px' }}>
                  {logoData.imageUrl ? (
                    <img
                      src={logoData.imageUrl}
                      alt={logoData.altText}
                      style={{ maxWidth: "200px", maxHeight: "80px", objectFit: 'contain' }}
                      className="img-fluid"
                    />
                  ) : (
                    <div className="text-center text-muted">
                      <i className="bi bi-image d-block fs-1 mb-2"></i>
                      <p>No logo uploaded</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="card-footer bg-white py-3 text-end">
              <button 
                onClick={handleSave} 
                disabled={saving}
                className="btn btn-primary px-4"
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-2"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoEditor;